import { FormInstance, Spin, message } from "antd";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  APIError,
  Category,
  Department,
  FormFieldConfig,
  SubCategory,
} from "../helpers/shared-interfaces";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import * as Sentry from "@sentry/browser";
import { AxiosResponse } from "axios";
import {
  GET_DEPARTMENT_CATEGORIES,
  GET_SUBCATEGORY,
  UPDATE_SUBCATEGORY,
} from "../gql/categories";
import { GET_DEPARTMENTS } from "../gql/departments";

interface RouteParams {
  id: string;
}

interface State {
  id: string;
  name: string;
  isLoading: boolean;
  isActive: boolean;
  departments: Department[];
  department?: Department;
  departmentId?: string;
  categories: Category[];
  category?: Category;
  subCategory?: SubCategory;
  sla: number;
  slaCreateByEod: number;
}

export default class EditSubCategory extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      id: "",
      name: "",
      isLoading: true,
      isActive: false,
      departments: [],
      department: undefined,
      categories: [],
      subCategory: undefined,
      category: undefined,
      sla: 0,
      slaCreateByEod: 0,
    };
    this.reference = React.createRef<FormInstance>();
    this.getSubCategory = this.getSubCategory.bind(this);
    this.updateSubCategory = this.updateSubCategory.bind(this);
    this.getDepartmentCategories = this.getDepartmentCategories.bind(this);
    this.getAllDepartments = this.getAllDepartments.bind(this);
  }

  async componentDidMount(): Promise<void> {
    this.setState({ id: this.props.match.params.id }, () => {
      this.getSubCategory();
      this.getAllDepartments();
    });
  }

  getSubCategory = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_SUBCATEGORY,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            this.setState({
              subCategory: data.data.subCategory,
              name: data.data.subCategory?.name,
              department: data.data.subCategory?.department,
              category: data.data.subCategory?.category,
              isActive: data.data.subCategory?.isActive,
            });
          } else {
            message.error("Failed to get subCategory data");
          }
        });
      },
    );
  };

  getDepartmentCategories = async (): Promise<void> => {
    const { departmentId } = this.state;

    this.setState({ isLoading: true });

    await request({
      query: GET_DEPARTMENT_CATEGORIES,
      variables: { departmentId },
    })
      .then(({ data: { data } }: any): void => {
        this.setState({
          categories: data?.categories,
          isLoading: false,
        });
      })
      .catch((error) => {
        message.error("Failed To Load Categories");
        Sentry.captureException(error);
      });

    this.setState({ isLoading: false });
  };

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  changeIsActive = async (checkedValue: boolean) => {
    this.setState({
      isActive: checkedValue,
    });
  };

  redirectToViewSubcategoryPage(): void {
    this.props.history.push(`/subCategories/${this.state.id}`);
  }

  getAllDepartments = (): void => {
    request({
      query: GET_DEPARTMENTS,
    }).then(({ data: { data } }: any): void => {
      this.setState({
        departments: data.departments,
        isLoading: false,
      });
    });
  };

  handleDepartmentSelect = async (departmentId: string): Promise<void> => {
    const department = this.state.departments.find(
      (dept) => dept.id === departmentId,
    );
    this.setState({ departmentId: department?.id }, () => {
      this.getDepartmentCategories();
    });
  };

  async updateSubCategory(): Promise<void> {
    const { id, isActive } = this.state;
    interface Response {
      data?: {
        updateSubCategory: {
          subCategory: SubCategory;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;
    let data;
    try {
      data = await this.reference.current.validateFields([
        "name",
        "sla",
        "slaCreateByEod",
        "categoryId",
        "isActive",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_SUBCATEGORY,
        variables: {
          subCategoryId: id,
          ...data,
          isActive,
        },
      });

      const { err, handled } = responseHandler<Response>(
        response,
        "updateSubCategory",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("Subcategory updated sucessfully");
        this.redirectToViewSubcategoryPage();
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Failed to update subCategory - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  }

  render(): JSX.Element | null {
    const {
      isLoading,
      departments,
      subCategory,
      categories,
      category,
      isActive,
    } = this.state;
    const fields: FormFieldConfig[] = [
      {
        name: "departmentId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Department",
        placeholder: "Department",
        initialValue: category?.department?.id,
        changeHandler: this.handleDepartmentSelect,
        selectHandler: this.handleDepartmentSelect,
        selectOptions: departments,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "categoryId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Category",
        placeholder: "Category",
        initialValue: category?.id,
        selectOptions: categories,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "name",
        name: "name",
        rule: "string",
        inputType: "string",
        required: true,
        label: "Name",
        initialValue: subCategory?.name,
        placeholder: "Name",
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "sla",
        name: "sla",
        rule: "integer",
        inputType: "number",
        required: true,
        label: "SLA",
        initialValue: subCategory?.sla,
        placeholder: "SLA",
        elementStyle: {
          width: '100%'
        },
        colConfig: {
          span: 7,
        },
      },
      {
        id: "slaCreateByEod",
        name: "slaCreateByEod",
        rule: "integer",
        inputType: "number",
        required: true,
        initialValue: subCategory?.slaCreateByEod,
        label: "Calculate SLA Before",
        elementStyle: {
          width: '100%'
        },
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "isActive",
        name: "isActive",
        rule: "boolean",
        inputType: "checkbox",
        required: false,
        label: "Is Active?",
        checkboxCheckedValue: isActive,
        checkboxChangeHandler: (e) => this.changeIsActive(e.target.checked),
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "updateSubCategory",
        name: "updateSubCategoryButton",
        inputType: "button",
        value: "Save",
        required: false,
        className: "base-button",
        elementStyle: { position: "relative", top: "3px" },
        colConfig: {
          span: 24,
        },
        handleClick: this.updateSubCategory,
      },
    ];

    return (
      <Card header="UPDATE SUBCATEGORY">
        <div>
          <Spin spinning={isLoading}>
            <BaseForm
              layout="vertical"
              reference={this.reference}
              fieldsToHide={[]}
              fields={fields}
            />
          </Spin>
        </div>
      </Card>
    );
  }
}
